import React, { useState, useEffect, useRef } from "react";
import { QueryBuilder, RuleGroupType, formatQuery, defaultControlElements, Controls, Field, ValueEditorProps, OperatorSelectorProps, RuleType, FullField, FieldSelectorProps } from "react-querybuilder";
import "../css/others/FilterPage.css";
import "../css/others/Table.css";
interface Attribute { name: string; label: string; type: string; }
interface SQLQueryGeneratorProps { attributes: Attribute[]; onQueryChange: (query: RuleGroupType) => void; query: RuleGroupType; disabled?: boolean; }
const SQLQueryGenerator: React.FC<SQLQueryGeneratorProps> = ({ attributes, onQueryChange, query: parentQuery, disabled = false, }) => {
  const [query, setQuery] = useState<RuleGroupType>(parentQuery);
  const [activeInput, setActiveInput] = useState<string | null>(null);
  useEffect(() => { setQuery(parentQuery) }, [parentQuery]);
  const handleQueryChange = (newQuery: RuleGroupType) => {
    if (disabled) return;
    const usedFields = new Set<string>();
    const updatedRules = newQuery.rules
      .map((rule) => {
        if ("field" in rule) {
          if (usedFields.has(rule.field)) {
            const nextAvailable = attributes.find((f) => !usedFields.has(f.name));
            if (nextAvailable) {
              usedFields.add(nextAvailable.name);
              return { ...rule, field: nextAvailable.name };
            }
            return null;
          }
          usedFields.add(rule.field);
        }
        return rule;
      })
      .filter((rule): rule is RuleType => rule !== null);
    if (updatedRules.length > attributes.length) return;
    const updatedQuery = { ...newQuery, rules: updatedRules };
    setQuery(updatedQuery);
    onQueryChange(updatedQuery);
  };
  const fields: Field[] = attributes.map((attr) => ({
    name: attr.name, label: attr.label, value: attr.name, inputType: attr.type === "date" ? "date" : "text",
    operators: [{ name: "=", label: "=" }, { name: "<", label: "<" }, { name: ">", label: ">" }, { name: "<=", label: "<=" }, { name: ">=", label: ">=" }, { name: "!=", label: "!=" },],
  }));
  const CustomValueEditor: React.FC<ValueEditorProps & { isFocused: boolean }> = ({ handleOnChange, value, field, operator, isFocused, }) => {
    const fieldDefinition = fields.find((f) => f.name === field);
    const fieldType = fieldDefinition?.inputType;
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      if (isFocused && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isFocused]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index?: number) => {
      const newValue = e.target.value;
      if (fieldType === "date" && operator === "between" && index !== undefined) {
        const [startDate, endDate] = Array.isArray(value) ? value : ["", ""];
        const updatedValue = index === 0 ? [newValue, endDate] : [startDate, newValue];
        handleOnChange(updatedValue);
      } else {
        handleOnChange(newValue);
      }
    };
    const renderInput = (inputType: string, inputValue: string, index?: number) => (
      <input ref={inputRef} type={inputType} value={inputValue || ""} onChange={(e) => handleChange(e, index)} onFocus={() => setActiveInput(field as string)} />
    );
    if (fieldType === "date") {
      if (operator === "between") {
        const [startDate, endDate] = Array.isArray(value) ? value : ["", ""];
        return (
          <div className="custom-date-between-container">
            {renderInput("date", startDate, 0)}
            <span className="custom-date-between-separator">and</span>
            {renderInput("date", endDate, 1)}
          </div>
        );
      }
      return renderInput("date", value as string);
    }
    return renderInput("text", value as string);
  };
  const CustomOperatorSelector: React.FC<OperatorSelectorProps> = ({ handleOnChange, value, }) => {
    const allowedOperators = ["<=", ">=", "=", "<", ">", "!="];
    return (
      <select className="custom-operator-selector" value={value} onChange={(e) => handleOnChange(e.target.value)}>
        {allowedOperators.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
    );
  };
  const isOption = (
    opt: any
  ): opt is { value: string; label: string } =>
    typeof opt?.value === "string" && typeof opt?.label === "string";
  const CustomFieldSelector: React.FC<FieldSelectorProps> = ({ handleOnChange, options, value, }) => {
    const currentField = value;
    const usedFields = query.rules
      .filter((r): r is RuleType => "field" in r && r.field !== currentField)
      .map((r) => r.field);
    const filteredOptions = (options as any[])
      .filter(isOption)
      .filter(
        (opt) =>
          !usedFields.includes(opt.value) || opt.value === currentField
      );
    return (
      <select className="custom-fields-selector" value={currentField} onChange={(e) => handleOnChange(e.target.value)}>
        <option value="">-- Select Field --</option>
        {filteredOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  };
  const customControlElements: Controls<FullField, string> = {
    ...defaultControlElements,
    fieldSelector: CustomFieldSelector,
    valueEditor: (props) => (
      <CustomValueEditor {...props} isFocused={activeInput === props.field} />
    ),
    operatorSelector: CustomOperatorSelector,
    combinatorSelector: () => null,
    addGroupAction: () => null,
  };
  return (
    <div>
      <div className="display-screen">
        <p>
          {query.rules.length === 0 ? "No Filter Applied" : formatQuery(query, { format: "sql", fields })}
        </p>
      </div>
      <div className="custom-query-builder">
        <QueryBuilder
          fields={fields}
          query={query}
          onQueryChange={handleQueryChange}
          controlElements={customControlElements}
          controlClassnames={{
            queryBuilder: "custom-query-builder",
            ruleGroup: "custom-rule-group",
            rule: "custom-rule",
            fields: "custom-fields-selector",
            operators: "custom-operators-selector",
            value: "custom-value-editor",
            removeRule: "custom-remove-rule",
            addRule: "custom-add-rule",
          }}
        />
      </div>
    </div>
  );
};
export default SQLQueryGenerator;